/* Customer reviews wall
   Two rows of review cards drift slowly in opposite directions. Each row is a
   native horizontal scroller, so swipe, trackpad, keyboard focus and mouse drag
   all move it. The drift is only the scroll position being advanced a little
   each frame, and it lets go the moment someone takes hold.

   To loop without a visible jump, each row's cards are cloned once (more on very
   wide screens) and the scroll position wraps by exactly one set's width. Clones
   are aria-hidden and inert, so screen readers and the tab order only ever meet
   the real cards.

   The drift stops under the pointer, while anything in a row has focus, for a
   few seconds after a manual scroll or drag, while the section is off screen or
   the tab is hidden, for visitors who prefer reduced motion, and whenever the
   pause control is pressed. */
(function () {
  'use strict';
  if (window.HomeReviewsInit) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  var HOLD_AFTER_MANUAL = 2600;
  var BOTTOM_ROW_RATIO = 0.82;

  function qa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function now() { return window.performance && performance.now ? performance.now() : Date.now(); }

  function Row(el, speed) {
    this.el = el;
    this.track = el.querySelector('[data-reviews-track]');
    this.dir = el.getAttribute('data-direction') === 'right' ? -1 : 1;
    this.speed = speed;
    this.pos = 0;
    this.base = 0;
    this.set = 0;
    this.written = -1;
    this.hover = false;
    this.focus = false;
    this.dragging = false;
    this.heldUntil = 0;
    this.originals = this.track ? Array.prototype.slice.call(this.track.children) : [];
    this.clones = [];
  }

  Row.prototype.addSet = function () {
    var self = this;
    this.originals.forEach(function (li) {
      var c = li.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('inert', '');
      c.setAttribute('data-clone', '');
      c.removeAttribute('data-shopify-editor-block');
      qa(c, 'a, button, input, [tabindex]').forEach(function (n) { n.setAttribute('tabindex', '-1'); });
      qa(c, '[id]').forEach(function (n) { n.removeAttribute('id'); });
      self.track.appendChild(c);
      self.clones.push(c);
    });
  };

  // The row is position: relative, so offsetLeft is measured from the row's own
  // padding edge in content coordinates: the same frame as scrollLeft, and
  // unaffected by how far the row has scrolled.
  Row.prototype.measure = function () {
    if (!this.clones.length) { this.set = 0; return; }
    this.base = this.originals[0].offsetLeft;
    this.set = this.clones[0].offsetLeft - this.base;
  };

  // Keep at least one full set beyond the visible width, so the wrap is never seen.
  Row.prototype.fill = function () {
    var guard = 0;
    while (this.set > 0 && this.el.scrollWidth < this.base + this.set + this.el.clientWidth + 2 && guard < 6) {
      this.addSet();
      guard++;
    }
  };

  Row.prototype.enableLoop = function () {
    if (!this.originals.length || this.clones.length) return;
    this.addSet();
    this.measure();
    this.fill();
    // Bottom row starts part-way through its set so the two rows never line up.
    this.pos = this.base + (this.dir < 0 ? this.set * 0.5 : 0);
    this.el.scrollLeft = this.pos;
    this.written = this.el.scrollLeft;
  };

  Row.prototype.disableLoop = function () {
    this.clones.forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); });
    this.clones = [];
    this.set = 0;
  };

  Row.prototype.held = function (t) {
    return this.hover || this.focus || this.dragging || t < this.heldUntil;
  };

  Row.prototype.step = function (t, dt, running) {
    if (!this.set) return;
    var el = this.el;
    var actual = el.scrollLeft;

    // Someone scrolled it (touch, trackpad, keyboard focus): follow them.
    if (Math.abs(actual - this.written) > 1.5) {
      this.pos = actual;
      this.heldUntil = t + HOLD_AFTER_MANUAL;
    }

    var held = this.held(t);
    if (running && !held) this.pos += this.dir * this.speed * dt / 1000;

    if (this.focus) {
      // Never wrap while a real card has keyboard focus: the jump would swap
      // the focused card for its hidden clone and leave focus off screen.
    } else if (held) {
      // While a person is steering, only wrap at the very ends, so momentum
      // scrolling is not interrupted mid-flight.
      var max = el.scrollWidth - el.clientWidth;
      if (this.pos <= 0.5) this.pos += this.set;
      else if (this.pos >= max - 0.5) this.pos -= this.set;
    } else {
      if (this.pos >= this.base + this.set) this.pos -= this.set;
      else if (this.pos < this.base) this.pos += this.set;
    }

    if (Math.abs(actual - this.pos) >= 0.5) el.scrollLeft = this.pos;
    this.written = el.scrollLeft;
  };

  function Wall(root) {
    this.root = root;
    this.autoplay = root.getAttribute('data-autoplay') !== 'false';
    var speed = parseFloat(root.getAttribute('data-speed')) || 24;
    this.rows = qa(root, '[data-reviews-row]').map(function (el, i) {
      return new Row(el, i === 0 ? speed : speed * BOTTOM_ROW_RATIO);
    });
    this.toggle = root.querySelector('[data-reviews-toggle]');
    this.toggleLabel = root.querySelector('[data-reviews-toggle-label]');
    this.userPaused = false;
    this.visible = false;
    this.raf = 0;
    this.last = 0;
    this.listeners = [];
    this.frame = this.frame.bind(this);
    this.init();
  }

  Wall.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts || false);
    this.listeners.push([target, type, fn, opts || false]);
  };

  Wall.prototype.motionAllowed = function () {
    return this.autoplay && !REDUCED.matches;
  };

  Wall.prototype.init = function () {
    var self = this;
    if (!this.rows.length) return;

    this.rows.forEach(function (row) { self.bindRow(row); });

    if (this.toggle) {
      this.on(this.toggle, 'click', function () {
        self.userPaused = !self.userPaused;
        self.syncToggle();
      });
    }

    var onMotionChange = function () { self.applyMotionPreference(); };
    if (REDUCED.addEventListener) this.on(REDUCED, 'change', onMotionChange);
    else if (REDUCED.addListener) REDUCED.addListener(onMotionChange);
    this.on(document, 'visibilitychange', function () { self.kick(); });

    var measure = function () { self.rows.forEach(function (r) { r.measure(); r.fill(); }); };
    this.on(window, 'resize', measure, { passive: true });

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        self.kick();
      }, { rootMargin: '120px 0px' });
      this.io.observe(this.root);
    } else {
      this.visible = true;
    }

    this.applyMotionPreference();
  };

  Wall.prototype.bindRow = function (row) {
    var el = row.el;
    var self = this;

    this.on(el, 'mouseenter', function () { if (FINE.matches) row.hover = true; });
    this.on(el, 'mouseleave', function () { row.hover = false; row.heldUntil = now() + 600; });
    // Keyboard focus: show the whole card, clear of the edge fade. Browsers only
    // scroll a focused element into view when it is fully hidden, so a card
    // half under the fade would otherwise take focus without being readable.
    this.on(el, 'focusin', function (e) {
      row.focus = true;
      var li = e.target.closest ? e.target.closest('li') : null;
      if (!li || li.parentNode !== row.track || li.hasAttribute('data-clone')) return;
      var margin = 48;
      var left = li.offsetLeft;
      var right = left + li.offsetWidth;
      var target = null;
      if (left - margin < el.scrollLeft) target = left - margin;
      else if (right + margin > el.scrollLeft + el.clientWidth) target = right + margin - el.clientWidth;
      if (target !== null) {
        el.scrollLeft = Math.max(0, target);
        row.pos = el.scrollLeft;
        row.written = el.scrollLeft;
      }
    });
    this.on(el, 'focusout', function (e) {
      if (!e.relatedTarget || !el.contains(e.relatedTarget)) { row.focus = false; row.heldUntil = now() + 1200; }
    });

    // Mouse drag. Touch and pens already scroll natively.
    var startX = 0;
    var startScroll = 0;
    var moved = false;
    var pointerId = null;

    var onMove = function (e) {
      if (e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) { moved = true; el.classList.add('is-dragging'); }
      if (moved) {
        el.scrollLeft = startScroll - dx;
        row.pos = el.scrollLeft;
        row.written = el.scrollLeft;
      }
    };
    var onUp = function (e) {
      if (e.pointerId !== pointerId) return;
      pointerId = null;
      row.dragging = false;
      row.heldUntil = now() + HOLD_AFTER_MANUAL;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      // Let the click that ends a drag fall on nothing.
      setTimeout(function () { el.classList.remove('is-dragging'); }, 0);
    };

    if (FINE.matches) el.classList.add('is-draggable');

    this.on(el, 'pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      moved = false;
      row.dragging = true;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    });
    this.on(el, 'click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
    this.on(el, 'dragstart', function (e) { e.preventDefault(); });
  };

  Wall.prototype.applyMotionPreference = function () {
    var allowed = this.motionAllowed();
    this.rows.forEach(function (r) { if (allowed) r.enableLoop(); else r.disableLoop(); });
    if (this.toggle) this.toggle.hidden = !allowed;
    this.syncToggle();
    this.kick();
  };

  Wall.prototype.syncToggle = function () {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
    if (this.toggleLabel) this.toggleLabel.textContent = this.userPaused ? 'Play reviews' : 'Pause reviews';
    this.kick();
  };

  Wall.prototype.shouldRun = function () {
    return this.visible && !document.hidden && this.motionAllowed();
  };

  Wall.prototype.kick = function () {
    if (this.shouldRun()) {
      if (!this.raf) { this.last = 0; this.raf = requestAnimationFrame(this.frame); }
    } else if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  };

  Wall.prototype.frame = function (t) {
    var dt = this.last ? Math.min(64, t - this.last) : 0;
    this.last = t;
    var running = !this.userPaused;
    for (var i = 0; i < this.rows.length; i++) this.rows[i].step(t, dt, running);
    this.raf = this.shouldRun() ? requestAnimationFrame(this.frame) : 0;
  };

  Wall.prototype.hold = function (blockId, on) {
    this.rows.forEach(function (row) {
      var li = row.originals.filter(function (n) { return n.getAttribute('data-block-id') === blockId; })[0];
      if (!li) return;
      row.heldUntil = on ? Infinity : now() + 800;
      if (on) {
        var target = li.offsetLeft - (row.el.clientWidth - li.offsetWidth) / 2;
        row.el.scrollLeft = Math.max(0, target);
        row.pos = row.el.scrollLeft;
        row.written = row.el.scrollLeft;
      }
    });
  };

  Wall.prototype.destroy = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.io) this.io.disconnect();
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
    this.rows.forEach(function (r) { r.disableLoop(); });
  };

  function init(root) {
    if (!root || root.__homeReviews) return;
    root.__homeReviews = new Wall(root);
  }
  function initAll(scope) { qa(scope || document, '[data-home-reviews]').forEach(init); }

  window.HomeReviewsInit = initAll;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    qa(e.target, '[data-home-reviews]').forEach(function (root) {
      if (root.__homeReviews) { root.__homeReviews.destroy(); root.__homeReviews = null; }
    });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest && e.target.closest('[data-home-reviews]');
    if (root && root.__homeReviews) root.__homeReviews.hold(e.detail && e.detail.blockId, true);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var root = e.target.closest && e.target.closest('[data-home-reviews]');
    if (root && root.__homeReviews) root.__homeReviews.hold(e.detail && e.detail.blockId, false);
  });
})();
